import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppNav from '../components/AppNav';
import ProfileInfo from '../components/profile/ProfileInfo';
import ProfileActions from '../components/profile/ProfileActions';
import ProfileGallery from '../components/profile/ProfileGallery';
import { apiClient } from '../api';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useOnlineUsers } from '../hooks/useOnlineUsers';

const ProfileDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { online } = useOnlineUsers();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [match, setMatch] = useState(false);
  const [totalLikes, setTotalLikes] = useState(0);
  const [favorited, setFavorited] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [prof, likeState, favState] = await Promise.all([
          apiClient.get(`/profiles/${userId}`),
          apiClient.get(`/likes/check/${userId}`),
          apiClient.get(`/favorites/check/${userId}`),
        ]);
        setProfile(prof.data);
        setLiked(likeState.data.i_liked);
        setMatch(likeState.data.match);
        setTotalLikes(likeState.data.total_likes || 0);
        setFavorited(favState.data.favorited);
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const handleFavorite = async () => {
    setActionLoading('fav');
    try {
      if (favorited) {
        await apiClient.delete(`/favorites/${userId}`);
        setFavorited(false);
        toast('Removed from favorites');
      } else {
        await apiClient.post(`/favorites/${userId}`);
        setFavorited(true);
        toast.success('Added to favorites', { description: `${profile.name} saved to your favorites.` });
      }
    } catch (err) {
      toast.error('Action failed', { description: err.response?.data?.detail || '' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleLike = async () => {
    setActionLoading('like');
    try {
      if (liked) {
        await apiClient.delete(`/likes/${userId}`);
        setLiked(false);
        setMatch(false);
        setTotalLikes((c) => Math.max(0, c - 1));
        toast('Removed like');
      } else {
        const res = await apiClient.post(`/likes/${userId}`);
        setLiked(true);
        setTotalLikes((c) => c + 1);
        if (res.data.match) {
          setMatch(true);
          toast.success("It's a match!", { description: `You and ${profile.name} liked each other.` });
        } else {
          toast('Liked', { description: `${profile.name} will be notified if they like you back.` });
        }
      }
    } catch (err) {
      toast.error('Action failed', { description: err.response?.data?.detail || '' });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePoke = async () => {
    setActionLoading('poke');
    try {
      await apiClient.post(`/poke/${userId}`);
      toast('Poked!', { description: `${profile.name} got your poke.` });
    } catch (err) {
      toast.error('Poke failed', { description: err.response?.data?.detail || '' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-fnl-dark">
        <AppNav />
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-fnl-pink border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-fnl-dark">
        <AppNav />
        <div className="text-center py-20 text-white">Profile not found.</div>
      </div>
    );
  }

  const isOnline = online.has(profile.user_id);

  return (
    <div className="min-h-screen bg-fnl-dark">
      <AppNav />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-8">
        <Button onClick={() => navigate(-1)} variant="ghost" className="text-white hover:text-fnl-pink hover:bg-white/5 mb-5" data-testid="profile-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to browse
        </Button>

        <div className="grid md:grid-cols-[360px_1fr] gap-8 bg-fnl-purple/60 rounded-3xl p-6 md:p-8 border border-purple-900/40">
          <ProfileInfo
            profile={profile}
            isOnline={isOnline}
            match={match}
            liked={liked}
            totalLikes={totalLikes}
            actionLoading={actionLoading}
            onLike={handleLike}
            onPoke={handlePoke}
            actions={
              <ProfileActions
                profile={profile}
                favorited={favorited}
                actionLoading={actionLoading}
                onFavorite={handleFavorite}
              />
            }
          />
        </div>

        <ProfileGallery name={profile.name} photos={profile.gallery_photos || []} />
      </div>
    </div>
  );
};

export default ProfileDetail;
